import { DefaultButton } from '@/components/ui/button/DefaultButton'
import { PrimaryButton } from '@/components/ui/button/PrimaryButton'
import {
  ControlsWrapper,
  FormControls
} from '@/components/ui/form/FormControls'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Pagination from '@/ui/components/Pagination/Pagination'
import useResponsive from '@/hooks/useResponsive'
import { formatToReport } from '@/utils/helpers/dates.helpers'
import { CSVLink } from 'react-csv'
import { printPDF } from '@/utils/func/print'
import MobileReportItem from './MobileReportItem'
import { useUsers } from '@/api/hooks/useUsers'
import { translate } from '@/i18n'
import { useIntl } from 'react-intl'

type IColumn = {
  label: string
  key: string
  alias: string
}

const employeeHeaders: IColumn[] = [
  { label: 'Пользователь', key: 'display', alias: 'user' }
]

const EmployeesReportData = ({ settings, onClose, userProps }) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const { isDesktop } = useResponsive()
  const intl = useIntl()
  const perPage = 10

  // Берем данные с бэка по странице
  const { data } = useUsers({ page: currentPage, perPage })

  const columns = useMemo(() => 
    employeeHeaders
      .concat(userProps)
      .filter((header) => settings.cols.includes(header.key))
      .map((item) => ({
        ...item,
        label: intl.formatMessage({ id: item.label })
      })), 
    [employeeHeaders, userProps, settings.cols, intl]
  )

  const pdfCols = useMemo(
    () => columns.map((col) => ({ header: col.label, dataKey: col.key })),
    [columns]
  )

  const dataItems = data?.items || []

  return (
    <ReportDataContainer>
      <Table>
        {isDesktop && (
          <TableHead>
            <tr>
              {columns.map((header) => (
                <TableHeadCell key={header.key}>{header.label}</TableHeadCell>
              ))}
            </tr>
          </TableHead>
        )}

        <TableBody>
          {dataItems.length ? (
            dataItems.map((item) => (
              <ElementItem columns={columns} key={item.id} item={item} />
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>
                <NotFound>{translate('no-results')}</NotFound>
              </td>
            </tr>
          )}
        </TableBody>
      </Table>

      <Pagination
        inverse
        currentPage={currentPage}
        total={data?.total || 0}
        itemsPerPage={perPage}
        handlePageChange={setCurrentPage}
      />

      <ControlsWrapper>
        <FormControls>
          <PrimaryButton
            type="button"
              style={{
                color: '#C12423',
                background: '#fff',
                border: '1px solid #C12423'
              }}
            onClick={() =>
              printPDF({
                name:
                  intl.formatMessage({ id: 'create-report-' + settings.type }) +
                  ` ${formatToReport(new Date())}`,
                columns: pdfCols,
                body: dataItems
              })
            }
          >
            {translate('pdf')}
          </PrimaryButton>

          <CSVLink
            data={dataItems}
            headers={columns}
            separator={';'}
            filename={
              intl.formatMessage({ id: 'create-report-' + settings.type }) +
              ` ${formatToReport(new Date())}`
            }
          >
            <PrimaryButton
              type="button"
              style={{
                color: '#C12423',
                background: '#fff',
                border: '1px solid #C12423'
              }}
            >
              {translate('csv')}
            </PrimaryButton>
          </CSVLink>
        </FormControls>
      </ControlsWrapper>
    </ReportDataContainer>
  )
}

export default EmployeesReportData

// ------------------- STYLED -------------------

const ReportDataContainer = styled.div`
  margin-top: 32px;
`

const TableHeadCell = styled.th`
  text-align: left;
  padding: 5px 10px 5px 0;
`

const TableBody = styled.tbody`
    tr:nth-child(even) {
        background-color: #EFF0F4;
    }

    tr:nth-child(odd) {
        background-color: #ffffff;
    }
`

const TableHead = styled.thead`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #8E8E8E;
`

const Table = styled.table`
  margin: 1rem 0;
  overflow-y: auto;
  height: 100%;
  width: 100%;
  overflow-x: hidden;

  tr td:last-child {
    width: 1%;
    white-space: nowrap;
  }
`

const TableCell = styled.td`
  font-weight: 400;
  font-size: 16px;
  line-height: 32px;
  padding: 5px 10px 5px 0;
`

const ElementItem: React.FC<{ item: any; columns: IColumn[] }> = ({
  item,
  columns
}) => {
  const { isDesktop } = useResponsive()

  if (!isDesktop) return <MobileReportItem columns={columns} data={item} />

  return (
    <tr>
      {columns.map((col) => (
        <TableCell key={col.key}>{item[col.key] || ''}</TableCell>
      ))}
    </tr>
  )
}

const NotFound = styled.div`
  width: 100%;
  text-align: center;
  padding: 12px 0;
`


